/**
 * Relationship statement draft builder (staff-only tool, 2026-07-19).
 *
 * Assembles the client's lodgement-stage answers into the section structure of
 * ADI's Relationship Statement template (Fillable Version_Relationship
 * Statement Template.pdf): First meeting, Relationship development, Date of
 * mutual commitment, Domestic arrangements, Significant events, Periods of
 * separation, Future plans.
 *
 * Output is an EDITABLE draft (Word-compatible .doc download built from HTML)
 * that staff polish against the branded template before uploading it to the
 * relationship_history_statement checklist slot. It is a drafting aid, never
 * auto-lodged and never client-visible.
 *
 * Sources (all captured at the lodgement gate):
 *   First meeting            <- applicant.relationship.statement.first_meeting
 *   Relationship development <- applicant.relationship.development
 *   Date of mutual commitment<- applicant.relationship.statement.mutual_commitment
 *                               (+ the date fact applicant.relationship.date_committed_shared_life)
 *   Domestic arrangements    <- financial_aspects + nature_of_household (+ nature_of_commitment)
 *   Significant events       <- applicant.relationship.statement.significant_events
 *   Periods of separation    <- lived_separately / separation_details
 *   Future plans             <- applicant.evidence.commitment.future_plans
 */

import { readNestedPath } from './schema-runtime/formStateAdapter';
import type { FormState } from './schema-runtime';

export interface StatementSection {
  title: string;
  prompt: string;
  answer: string;
  /** True when the client has not answered the underlying question(s) yet. */
  missing: boolean;
}

export interface RelationshipStatementDraft {
  applicantName: string;
  sponsorName: string;
  sections: StatementSection[];
  missingCount: number;
}

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

function joinName(state: FormState, base: string): string {
  const given = str(readNestedPath(state, `${base}.given_names`));
  const family = str(readNestedPath(state, `${base}.family_name`));
  return [given, family].filter(Boolean).join(' ');
}

export function buildRelationshipStatementDraft(state: FormState): RelationshipStatementDraft {
  const r = (path: string) => str(readNestedPath(state, path));

  const commitmentDate = r('applicant.relationship.date_committed_shared_life');
  const mutualCommitment = [commitmentDate ? `Date: ${commitmentDate}` : '', r('applicant.relationship.statement.mutual_commitment')]
    .filter(Boolean)
    .join('\n\n');

  const domestic = [
    r('applicant.relationship.financial_aspects'),
    r('applicant.relationship.nature_of_household'),
    r('applicant.relationship.nature_of_commitment'),
  ]
    .filter(Boolean)
    .join('\n\n');

  const livedSeparately = r('applicant.relationship.lived_separately');
  const separation =
    livedSeparately === 'No'
      ? 'We have not been separated for any period since committing to our relationship.'
      : r('applicant.relationship.separation_details');

  const sections: StatementSection[] = [
    {
      title: 'First meeting',
      prompt: 'How, when and where you first met',
      answer: r('applicant.relationship.statement.first_meeting'),
      missing: false,
    },
    {
      title: 'Relationship development',
      prompt: 'How your relationship developed',
      answer: r('applicant.relationship.development'),
      missing: false,
    },
    {
      title: 'Date of mutual commitment',
      prompt: 'When you decided to commit to a mutually exclusive relationship to the exclusion of all others',
      answer: mutualCommitment,
      missing: false,
    },
    {
      title: 'Domestic arrangements',
      prompt: 'How you support each other financially, physically and emotionally, and when this level of commitment began',
      answer: domestic,
      missing: false,
    },
    {
      title: 'Significant events',
      prompt: 'Significant events in your relationship',
      answer: r('applicant.relationship.statement.significant_events'),
      missing: false,
    },
    {
      title: 'Periods of separation',
      prompt: 'When and why any separation occurred, for how long, and how you maintained your relationship during it',
      answer: separation,
      missing: false,
    },
    {
      title: 'Future plans',
      prompt: 'Your plans together for the future',
      answer: r('applicant.evidence.commitment.future_plans'),
      missing: false,
    },
  ];
  for (const s of sections) s.missing = s.answer.length === 0;

  return {
    applicantName: joinName(state, 'applicant.name'),
    sponsorName: joinName(state, 'sponsor.name'),
    sections,
    missingCount: sections.filter((s) => s.missing).length,
  };
}

const esc = (t: string) =>
  t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');

/**
 * Word-compatible HTML document. Saved with a .doc extension it opens in
 * Word/Pages/Google Docs with headings intact, ready to polish and paste into
 * the branded template.
 */
export function relationshipStatementDocHtml(draft: RelationshipStatementDraft): string {
  const body = draft.sections
    .map(
      (s) => `
    <h2 style="color:#0B2341;font-family:Arial,sans-serif;">${esc(s.title.toUpperCase())}</h2>
    <p style="color:#2CABEA;font-style:italic;font-family:Arial,sans-serif;">${esc(s.prompt)}</p>
    <p style="font-family:Arial,sans-serif;">${s.missing ? '[NOT YET ANSWERED - complete before lodgement]' : esc(s.answer)}</p>`,
    )
    .join('\n');
  return `<html><head><meta charset="utf-8"><title>Relationship Statement</title></head><body>
    <h1 style="color:#0B2341;font-family:Arial,sans-serif;">Relationship Statement</h1>
    <p style="font-family:Arial,sans-serif;"><b>Applicant:</b> ${esc(draft.applicantName)}<br/>
    <b>Sponsor:</b> ${esc(draft.sponsorName)}</p>
    ${body}
  </body></html>`;
}
